"use client";
// Port Wingo UI (React 19 + Tailwind 4) → Tailwind 3.4, preluat din portul ERP-ului (React 18). Vezi components/wingo/README.md.

// Wingo UI Pro · https://wingo-ui.com/legal/license

import {
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type MouseEvent,
  type MutableRefObject,
  type PointerEvent,
  type ReactNode,
  type Ref,
} from "react";
import { AnimatePresence, motion, type Transition } from "motion/react";
import { Copy, X } from "lucide-react";
import { Button, type ButtonProps, type ButtonRadius, type ButtonSize } from "@/components/wingo/ui/button";
import { Tooltip } from "@/components/wingo/ui/tooltip";
import { useClipboard, type CopySource } from "@/components/wingo/hooks/use-clipboard";
import { durations, easings, springs, usePrefersReducedMotion } from "@/components/wingo/lib/motion";
import { toneClasses, type Tone } from "@/components/wingo/lib/tones";
import { useDefaults } from "@/components/wingo/lib/ui-config";
import { cn } from "@/components/wingo/lib/utils";
import { withRef } from "@/components/wingo/lib/with-ref";

export type CopyButtonState = "idle" | "copied" | "error";
export type CopyButtonSize = "sm" | "md" | "lg";
export type CopyButtonSlot = "icon" | "label";
export type CopyTextSlot = "value" | "icon";
export type CopyButtonLabels = { copyLabel: string; copiedLabel: string; errorLabel: string };

export const DEFAULT_COPY_BUTTON_LABELS: CopyButtonLabels = {
  copyLabel: "Copy",
  copiedLabel: "Copied",
  errorLabel: "Copy failed",
};

const ICON_SIZES: Record<CopyButtonSize, ButtonSize> = { sm: "icon-sm", md: "icon", lg: "icon-lg" };

// a slow value (a key fetched on demand) shows the button's spinner only after this long, so fast copies never flash it
const PENDING_DELAY = 300;

// the one grid cell every swap shares, so nothing jumps when the icon or the label changes
const STACK = "grid place-items-center [grid-template-areas:'stack'] *:[grid-area:stack]";

// the check and the error mark paint through --tone-soft-foreground of their own tone
const markTone = (tone: Tone | undefined) =>
  tone && tone !== "neutral"
    ? cn(toneClasses[tone] ?? toneClasses.success, "text-[color:var(--tone-soft-foreground)]")
    : undefined;

// an empty value is a failure, never a silent copy of nothing
async function readValue(value: CopySource): Promise<string> {
  const text = typeof value === "function" ? await value() : value;
  if (!text) throw new Error("Nothing to copy");
  return text;
}

// one shared polite live region at the end of <body>. inside the button it would join the button's name (and a
// button's children are presentational), as a sibling it would break :last-child rules in groups and rows; radix
// modals keep [aria-live] nodes readable, so it also works from a dialog or a sheet
let region: HTMLElement | null = null;
let announceTimer: ReturnType<typeof setTimeout> | undefined;

function liveRegion() {
  if (region?.isConnected) return region;
  region = document.createElement("div");
  region.setAttribute("data-slot", "copy-button-status");
  region.setAttribute("role", "status");
  region.setAttribute("aria-live", "polite");
  region.setAttribute("aria-atomic", "true");
  region.style.cssText =
    "position:absolute;width:1px;height:1px;margin:-1px;padding:0;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0";
  document.body.appendChild(region);
  return region;
}

function announce(text: string) {
  const node = liveRegion();
  clearTimeout(announceTimer);
  // emptied first, so a second copy reads "Copied" again
  node.textContent = "";
  announceTimer = setTimeout(() => {
    node.textContent = text;
    announceTimer = setTimeout(() => (node.textContent = ""), 4000);
  }, 50);
}

function mergeRefs<T>(...refs: (Ref<T> | undefined)[]) {
  return (node: T | null) => {
    for (const ref of refs) {
      if (typeof ref === "function") ref(node);
      else if (ref) (ref as MutableRefObject<T | null>).current = node;
    }
  };
}

// ---------- the shared copy state ----------

type CopyStateOptions = {
  value: CopySource;
  html?: string;
  timeout: number;
  copiedLabel: string;
  errorLabel: string;
  onCopy?: (text: string) => void;
  onError?: (error: Error) => void;
};

function useCopyState({ value, html, timeout, copiedLabel, errorLabel, onCopy, onError }: CopyStateOptions) {
  const pointerRef = useRef<string>("mouse");
  const { copy, copied, error, reset } = useClipboard({
    timeout,
    onCopy: (text) => {
      // a short tick under the finger; desktops have nothing to vibrate
      if (pointerRef.current === "touch" || pointerRef.current === "pen") navigator.vibrate?.(10);
      onCopy?.(text);
    },
    onError,
  });
  const [count, setCount] = useState(0);
  const [pending, setPending] = useState(false);
  const busy = useRef(false);

  // the live region exists before the first copy, so the first announcement is read too
  useEffect(() => {
    liveRegion();
  }, []);

  // the error mark goes back to the copy icon after the same timeout as the check
  useEffect(() => {
    if (!error || timeout <= 0) return;
    const timer = setTimeout(reset, timeout);
    return () => clearTimeout(timer);
  }, [error, timeout, reset]);

  const run = async () => {
    if (busy.current) return false;
    busy.current = true;
    const slow = setTimeout(() => setPending(true), PENDING_DELAY);
    // a plain string is written straight from the click (safari only allows it inside the gesture); functions and
    // an empty value go through readValue, which turns "nothing to copy" into an error
    const source: CopySource = typeof value === "string" && value ? value : () => readValue(value);
    const ok = await copy(source, html ? { html } : undefined);
    announce(ok ? copiedLabel : errorLabel);
    clearTimeout(slow);
    setPending(false);
    busy.current = false;
    // a repeat copy redraws the check
    if (ok) setCount((n) => n + 1);
    return ok;
  };

  const state: CopyButtonState = error ? "error" : copied ? "copied" : "idle";
  return { state, count, pending, run, pointerRef };
}

// ---------- the icon swap ----------

// lucide's check, drawn from the short stroke to the long one
function DrawnCheck({ reduced }: { reduced: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <motion.path
        d="M4 12l5 5L20 6"
        initial={{ pathLength: reduced ? 1 : 0 }}
        animate={{ pathLength: 1 }}
        transition={{ ...springs.snappy, delay: 0.04 }}
      />
    </svg>
  );
}

type IconSwapProps = {
  state: CopyButtonState;
  count: number;
  icon?: ReactNode;
  copiedIcon?: ReactNode;
  errorIcon?: ReactNode;
  successTone?: Tone;
  errorTone?: Tone;
  className?: string;
  slot: string;
};

function IconSwap({
  state,
  count,
  icon,
  copiedIcon,
  errorIcon,
  successTone,
  errorTone,
  className,
  slot,
}: IconSwapProps) {
  const reduced = usePrefersReducedMotion();
  const fade: Transition = { duration: durations.fast, ease: easings.out };
  // copy -> check: the copy icon shrinks away while the check pops in on a bouncy spring; back to idle is snappy.
  // reduced motion keeps the crossfade and drops the scale
  const pop: Transition = reduced ? fade : { ...springs.bouncy, opacity: fade };
  const hidden = reduced ? { opacity: 0 } : { opacity: 0, scale: 0.5 };
  const shown = reduced ? { opacity: 1 } : { opacity: 1, scale: 1 };
  const leave = { ...hidden, transition: reduced ? fade : { ...springs.snappy, opacity: fade } };

  return (
    <span data-slot={slot} data-state={state} aria-hidden className={cn(STACK, "shrink-0", className)}>
      <motion.span
        className="inline-flex"
        initial={false}
        animate={state === "idle" ? shown : hidden}
        transition={reduced ? fade : { ...springs.snappy, opacity: fade }}
      >
        {icon ?? <Copy />}
      </motion.span>
      <AnimatePresence initial={false}>
        {state === "copied" && (
          <motion.span
            key={`copied-${count}`}
            className={cn("inline-flex", markTone(successTone))}
            initial={hidden}
            animate={shown}
            exit={leave}
            transition={pop}
          >
            {copiedIcon ?? <DrawnCheck reduced={reduced} />}
          </motion.span>
        )}
        {state === "error" && (
          <motion.span
            key="error"
            className={cn("inline-flex", markTone(errorTone))}
            initial={hidden}
            animate={shown}
            exit={leave}
            transition={pop}
          >
            {errorIcon ?? <X />}
          </motion.span>
        )}
      </AnimatePresence>
    </span>
  );
}

// ---------- CopyButton ----------

export type CopyButtonProps = Omit<
  ButtonProps,
  | "value"
  | "children"
  | "size"
  | "radius"
  | "onCopy"
  | "onError"
  | "classNames"
  | "asChild"
  // the copy icon is the only icon; the label swap owns the content
  | "leftIcon"
  | "rightIcon"
  | "spinner"
  | "loadingText"
> & {
  // the text, or a function read at click time (the latest form value, a key fetched on demand)
  value: CopySource;
  // a rich version copied next to the plain text
  html?: string;
  // a label turns it into a text button ("Copy IBAN"); without one it is a square icon button
  children?: ReactNode;
  // sm 32px, md 40px, lg 48px; icon-only maps to icon-sm / icon / icon-lg
  size?: CopyButtonSize;
  // auto: full (a circle) when icon-only, md with a label
  radius?: ButtonRadius | "auto";
  // aria-label and tooltip while idle
  copyLabel?: string;
  // tooltip, label and announcement after a copy
  copiedLabel?: string;
  // tooltip and announcement when the copy fails
  errorLabel?: string;
  // the desktop tooltip on icon-only buttons; touch screens never show it, the icon and the announcement carry the feedback
  tooltip?: boolean;
  // ms the check stays
  timeout?: number;
  icon?: ReactNode;
  copiedIcon?: ReactNode;
  errorIcon?: ReactNode;
  // color of the check; neutral keeps it the button's own color
  successTone?: Tone;
  onCopy?: (text: string) => void;
  onError?: (error: Error) => void;
  classNames?: Partial<Record<CopyButtonSlot, string>>;
};

function CopyButtonRender(copyButtonProps: CopyButtonProps) {
  const {
    value,
    html,
    children,
    variant = "ghost",
    size = "sm",
    radius = "auto",
    tone = "neutral",
    copyLabel = DEFAULT_COPY_BUTTON_LABELS.copyLabel,
    copiedLabel = DEFAULT_COPY_BUTTON_LABELS.copiedLabel,
    errorLabel = DEFAULT_COPY_BUTTON_LABELS.errorLabel,
    tooltip = true,
    timeout = 2000,
    icon,
    copiedIcon,
    errorIcon,
    successTone = "success",
    loading = false,
    disabled = false,
    onCopy,
    onError,
    onClick,
    onPointerDown,
    classNames,
    className,
    ref,
    ...props
  } = useDefaults("copy-button", copyButtonProps);
  const { state, count, pending, run, pointerRef } = useCopyState({
    value,
    html,
    timeout,
    copiedLabel,
    errorLabel,
    onCopy,
    onError,
  });
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [tipOpen, setTipOpen] = useState(false);
  // radix closes a tooltip on pointerdown and click; while a press is in flight that close is ignored, so the
  // tooltip can answer "Copied" in place
  const holding = useRef(false);
  const release = () => setTimeout(() => (holding.current = false), 60);

  const hasLabel = children !== undefined && children !== null && children !== false && children !== "";
  const safeSize: CopyButtonSize = Object.hasOwn(ICON_SIZES, size) ? size : "sm";
  const resolvedRadius: ButtonRadius = radius === "auto" ? (hasLabel ? "md" : "full") : radius;
  // a solid fill keeps the marks in its own text color; tinted marks on black or orange read as noise
  const solid = variant === "solid";
  const showTooltip = tooltip && !hasLabel;

  const handlePointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    onPointerDown?.(event);
    pointerRef.current = event.pointerType;
    if (event.pointerType !== "mouse") return;
    holding.current = true;
    document.addEventListener("pointerup", release, { once: true });
  };

  const handleClick = async (event: MouseEvent<HTMLButtonElement>) => {
    onClick?.(event);
    if (event.defaultPrevented) return;
    // Enter / Space arrive as a click with no pointer
    if (event.detail === 0) pointerRef.current = "keyboard";
    holding.current = true;
    release();
    await run();
    const el = buttonRef.current;
    // after a mouse or keyboard copy the tooltip says so, if the pointer or focus is still there
    if (
      showTooltip &&
      el &&
      pointerRef.current !== "touch" &&
      pointerRef.current !== "pen" &&
      el.matches(":hover, :focus-visible")
    ) {
      setTipOpen(true);
    }
  };

  const status = state === "copied" ? copiedLabel : state === "error" ? errorLabel : "";

  const button = (
    <Button
      {...props}
      ref={mergeRefs(ref as Ref<HTMLButtonElement> | undefined, buttonRef)}
      variant={variant}
      tone={tone}
      size={hasLabel ? safeSize : ICON_SIZES[safeSize]}
      radius={resolvedRadius}
      loading={loading || pending}
      disabled={disabled}
      aria-label={hasLabel ? props["aria-label"] : (props["aria-label"] ?? copyLabel)}
      data-slot="copy-button"
      data-state={state}
      className={className}
      onPointerDown={handlePointerDown}
      onClick={handleClick}
    >
      <IconSwap
        slot="copy-button-icon"
        state={state}
        count={count}
        icon={icon}
        copiedIcon={copiedIcon}
        errorIcon={errorIcon}
        successTone={solid ? undefined : successTone}
        errorTone={solid ? undefined : "danger"}
        className={classNames?.icon}
      />
      {hasLabel && (
        <SwapLabel
          copied={state === "copied"}
          label={children}
          copiedLabel={copiedLabel}
          className={classNames?.label}
        />
      )}
    </Button>
  );

  if (!showTooltip) return button;
  return (
    <Tooltip
      content={status || copyLabel}
      open={tipOpen}
      onOpenChange={(next) => {
        if (!next && holding.current) return;
        setTipOpen(next);
      }}
      // a long press on a copy button should not also open a tooltip
      longPress={false}
    >
      {button}
    </Tooltip>
  );
}

// the label and its copied twin share one cell, so the button keeps its width
function SwapLabel({
  copied,
  label,
  copiedLabel,
  className,
}: {
  copied: boolean;
  label: ReactNode;
  copiedLabel: ReactNode;
  className?: string;
}) {
  const reduced = usePrefersReducedMotion();
  const transition: Transition = reduced
    ? { duration: durations.fast, ease: easings.out }
    : { ...springs.snappy, opacity: { duration: durations.fast, ease: easings.out } };
  const offset = reduced ? 0 : 5;
  return (
    <span data-slot="copy-button-label" className={cn(STACK, "justify-items-start", className)}>
      <motion.span
        initial={false}
        animate={{ opacity: copied ? 0 : 1, y: copied ? -offset : 0 }}
        transition={transition}
      >
        {label}
      </motion.span>
      <motion.span
        aria-hidden
        initial={false}
        animate={{ opacity: copied ? 1 : 0, y: copied ? 0 : offset }}
        transition={transition}
      >
        {copiedLabel}
      </motion.span>
    </span>
  );
}

// ---------- CopyText ----------

export type CopyTextProps = Omit<ComponentProps<"button">, "value" | "onCopy" | "onError"> & {
  // what gets copied; defaults to children when it is a string or a number
  value?: CopySource;
  html?: string;
  // [font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace] at 12.5px, for ids, keys and IBANs
  mono?: boolean;
  // one line with an ellipsis; the whole value is still copied
  truncate?: boolean;
  // the title while idle
  copyLabel?: string;
  copiedLabel?: string;
  errorLabel?: string;
  timeout?: number;
  icon?: ReactNode;
  copiedIcon?: ReactNode;
  successTone?: Tone;
  onCopy?: (text: string) => void;
  onError?: (error: Error) => void;
  classNames?: Partial<Record<CopyTextSlot, string>>;
};

function CopyTextRender(copyTextProps: CopyTextProps) {
  const {
    value,
    html,
    mono = false,
    truncate = true,
    copyLabel = DEFAULT_COPY_BUTTON_LABELS.copyLabel,
    copiedLabel = DEFAULT_COPY_BUTTON_LABELS.copiedLabel,
    errorLabel = DEFAULT_COPY_BUTTON_LABELS.errorLabel,
    timeout = 2000,
    icon,
    copiedIcon,
    successTone = "success",
    onCopy,
    onError,
    onClick,
    onPointerDown,
    disabled = false,
    classNames,
    className,
    children,
    type = "button",
    ...props
  } = useDefaults("copy-text", copyTextProps);
  const text = value ?? (typeof children === "string" || typeof children === "number" ? String(children) : "");
  const { state, count, run, pointerRef } = useCopyState({
    value: text,
    html,
    timeout,
    copiedLabel,
    errorLabel,
    onCopy,
    onError,
  });
  const reduced = usePrefersReducedMotion();
  const status = state === "copied" ? copiedLabel : state === "error" ? errorLabel : "";

  return (
    <button
      type={type}
      disabled={disabled}
      title={status || copyLabel}
      data-slot="copy-text"
      data-state={state}
      data-disabled={disabled || undefined}
      className={cn(
        "relative -mx-1 inline-flex max-w-full min-w-0 cursor-pointer touch-manipulation items-baseline gap-1 rounded-[6px] px-1 text-start align-baseline text-inherit outline-hidden [-webkit-tap-highlight-color:transparent]",
        "decoration-[color:color-mix(in_oklab,currentColor_40%,transparent)] decoration-dotted underline-offset-4 hover:underline focus-visible:underline",
        "focus-visible:outline-2 focus-visible:outline focus-visible:outline-offset-1 focus-visible:outline-ring",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        // a 44px tall hit area on touch without moving the text
        "pointer-coarse:after:absolute pointer-coarse:after:inset-x-0 pointer-coarse:after:-inset-y-3",
        className,
      )}
      onPointerDown={(event) => {
        onPointerDown?.(event);
        pointerRef.current = event.pointerType;
      }}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        if (event.detail === 0) pointerRef.current = "keyboard";
        void run();
      }}
      {...props}
    >
      {/* one soft flash per copy */}
      {count > 0 && (
        <motion.span
          key={count}
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-[inherit] bg-foreground/5"
          initial={{ opacity: 1 }}
          animate={{ opacity: 0 }}
          transition={{ duration: reduced ? durations.fast : 0.4, ease: easings.out }}
        />
      )}
      <span
        data-slot="copy-text-value"
        className={cn("min-w-0", truncate && "truncate", mono && "[font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace] text-[12.5px]", classNames?.value)}
      >
        {children ?? (typeof value === "string" ? value : null)}
      </span>
      <IconSwap
        slot="copy-text-icon"
        state={state}
        count={count}
        icon={icon}
        copiedIcon={copiedIcon}
        successTone={successTone}
        errorTone="danger"
        className={cn(
          "size-3.5 self-center text-muted-foreground [&_svg:not([class*='size-'])]:size-3.5",
          classNames?.icon,
        )}
      />
    </button>
  );
}

// React 18: ref-urile ajung la randare doar prin forwardRef (vezi lib/with-ref.ts)
export const CopyButton = withRef(CopyButtonRender, "CopyButton");
export const CopyText = withRef(CopyTextRender, "CopyText");
